import { BaseApi } from './base.api.js';
import { ApiError } from '../errors/api-error.js';
import { UPLOAD_FILE_TIMEOUT_MS } from '@repo/contracts';
import { resolveOnUploadProgress, uploadAxiosConfig } from '../utils/upload-axios-config.js';

export class DocumentRevisionApi extends BaseApi {
    constructor(client, basePath = '/document') {
        super(client, basePath);
    }

    /**
     * Get all revisions for a document
     */
    async getRevisions(docId, params = {}) {
        try {
            const response = await this.client.get(`${this.basePath}/${docId}/revisions`, { params });
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    /**
     * Get active revision for a document
     */
    async getActiveRevision(docId) {
        try {
            const response = await this.client.get(`${this.basePath}/${docId}/revisions/active`);
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    /**
     * Get revision by ID
     */
    async getRevisionById(docId, revId) {
        try {
            const response = await this.client.get(`${this.basePath}/${docId}/revisions/${revId}`);
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    /**
     * Create new revision (file upload)
     * @param {Function|{ onUploadProgress?: Function }} [optionsOrCallback]
     */
    async createRevision(docId, formData, optionsOrCallback) {
        try {
            const response = await this.client.post(
                `${this.basePath}/${docId}/revisions`,
                formData,
                uploadAxiosConfig({
                    timeout: UPLOAD_FILE_TIMEOUT_MS,
                    onUploadProgress: resolveOnUploadProgress(optionsOrCallback),
                }),
            );
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    /**
     * Overwrite existing revision content in-place (file upload)
     * @param {Function|{ onUploadProgress?: Function }} [optionsOrCallback]
     */
    async updateRevisionContent(docId, revId, formData, optionsOrCallback) {
        try {
            const response = await this.client.put(
                `${this.basePath}/${docId}/revisions/${revId}/content`,
                formData,
                uploadAxiosConfig({
                    timeout: UPLOAD_FILE_TIMEOUT_MS,
                    onUploadProgress: resolveOnUploadProgress(optionsOrCallback),
                }),
            );
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    /**
     * Activate a revision
     */
    async activateRevision(docId, revId) {
        try {
            const response = await this.client.put(`${this.basePath}/${docId}/revisions/${revId}/activate`);
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    /**
     * Download revision file
     */
    async downloadRevision(docId, revId) {
        try {
            const response = await this.client.get(`${this.basePath}/${docId}/revisions/${revId}/download`, {
                responseType: 'blob'
            });
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    /**
     * Metadata-rich export (reporting server) via SGRC API proxy; requires canExport on document grants when used.
     */
    async exportRevisionWithMetadata(docId, revId, payload) {
        try {
            const response = await this.client.post(
                `${this.basePath}/${docId}/revisions/${revId}/export-with-metadata`,
                payload,
                { responseType: 'blob' },
            );
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    /**
     * Compare two revisions (Word documents only).
     * @param {string} docId
     * @param {string} revId
     * @param {string} targetRevId
     * @param {Record<string, string|boolean|number|undefined>} [compareQuery] — reporting-server DevExpress seçenekleri: author, ignoreFormatting, compareCaseChanges, comparisonLevel (word|character), acceptInputRevisions
     */
    async compareRevisions(docId, revId, targetRevId, compareQuery = {}) {
        try {
            const qs = new URLSearchParams();
            Object.entries(compareQuery || {}).forEach(([k, v]) => {
                if (v === undefined || v === null || v === '') return;
                qs.set(k, String(v));
            });
            const suffix = qs.toString() ? `?${qs.toString()}` : '';
            const response = await this.client.post(
                `${this.basePath}/${docId}/revisions/${revId}/compare/${targetRevId}${suffix}`,
                {},
                { responseType: 'blob' },
            );
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    /**
     * @param {Record<string, string|boolean|undefined>} [query] — pdfReviewMode: allMarkup | simpleMarkup | noMarkup; refresh: true ile önbellekteki PDF atlanıp yeniden üretilir
     */
    async getRevisionPdfBlob(docId, revId, query = {}) {
        try {
            const params = {
                pdfReviewMode: 'allMarkup',
                ...query,
            };
            Object.keys(params).forEach((k) => {
                if (params[k] === undefined || params[k] === null || params[k] === '') {
                    delete params[k];
                }
            });
            const response = await this.client.get(`${this.basePath}/${docId}/revisions/${revId}/pdf`, {
                params,
                responseType: 'blob',
                timeout: 180000,
            });
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    /**
     * Word compare (DevExpress) → tek PDF; revizyon işaretleri pdfReviewMode ile (ör. allMarkup).
     * @param {Record<string, string|boolean|number|undefined>} [query] — pdfReviewMode + compare seçenekleri (author, …)
     */
    async getCompareResultPdfBlob(docId, baseRevId, targetRevId, query = {}) {
        try {
            const params = {
                pdfReviewMode: 'allMarkup',
                ...query,
            };
            Object.keys(params).forEach((k) => {
                if (params[k] === undefined || params[k] === null || params[k] === '') {
                    delete params[k];
                }
            });
            const qs = new URLSearchParams();
            Object.entries(params).forEach(([k, v]) => {
                qs.set(k, String(v));
            });
            const suffix = qs.toString() ? `?${qs.toString()}` : '';
            const response = await this.client.post(
                `${this.basePath}/${docId}/revisions/${baseRevId}/compare/${targetRevId}/pdf${suffix}`,
                {},
                { responseType: 'blob', timeout: 300000 },
            );
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    /**
     * Convert revision to PDF, write beside source file, update revision.pdfUrl.
     * @param {Record<string, string|undefined>} [query] — pdfReviewMode
     */
    async persistRevisionPdf(docId, revId, query = {}) {
        try {
            const params = { ...query };
            Object.keys(params).forEach((k) => {
                if (params[k] === undefined || params[k] === null || params[k] === '') {
                    delete params[k];
                }
            });
            const response = await this.client.post(
                `${this.basePath}/${docId}/revisions/${revId}/pdf/persist`,
                {},
                { params, timeout: 180000 },
            );
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    /**
     * @param {Record<string, string|boolean|undefined>} [trackQuery] — trackFormatting, trackMoves (DevExpress DocumentTrackChangesOptions)
     */
    async applyTrackChanges(docId, revId, enabled, trackQuery = {}) {
        try {
            const qs = new URLSearchParams();
            qs.set('enabled', enabled ? 'true' : 'false');
            Object.entries(trackQuery || {}).forEach(([k, v]) => {
                if (v === undefined || v === null || v === '') return;
                qs.set(k, String(v));
            });
            const response = await this.client.post(
                `${this.basePath}/${docId}/revisions/${revId}/track-changes?${qs.toString()}`,
                {},
                { responseType: 'blob' },
            );
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    /**
     * Submit revision for approval
     */
    async submitForApproval(docId, revId) {
        try {
            const response = await this.client.post(`${this.basePath}/${docId}/revisions/${revId}/submit-approval`);
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    /**
     * Approve a revision step
     */
    async approveRevision(docId, revId, data = {}) {
        try {
            const response = await this.client.post(`${this.basePath}/${docId}/revisions/${revId}/approve`, data);
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    /**
     * Reject a revision step
     */
    async rejectRevision(docId, revId, data = {}) {
        try {
            const response = await this.client.post(`${this.basePath}/${docId}/revisions/${revId}/reject`, data);
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }
}
